import { type FC } from "react";

import { type MylensesSubmissionDetailsSource } from "@snapchat/blizzard-schema";
import { Control, Spacing, Status, TextArea } from "@snapchat/snap-design-system";
import { useIntl } from "react-intl";

import { useFeatureGates } from "../../../../cof-feature-gates/hooks";
import { CofConfigId, SurfaceType } from "../../../../gql-generated/types";
import { LENS_RELEASE_NOTE_MAX_CHARS } from "../../../../lens/components/constants";
import { SpectaclesTagsDescription } from "../../../../lens/components/lens-source/messages.generated";
import { LensDiscoverabilityTagsRow } from "../../../../lens-discoverability/components";
import { LensPreview } from "../../../../lens-preview/components/LensPreview";
import { LensPreviewToBeReviewed } from "../../../../lens-preview/components/messages.generated";
import { Tags } from "../../../../lens-tags/components";
import { Flexbox } from "../../../../shared/components";
import { type GetAllPreSubmissionInfoWithOrgResourcesQuery } from "../../../api/queries";
import { MAX_SNAPCHAT_TAGS, MAX_SPECTACLES_TAGS } from "../../constants";
import { LensDetailsIcon } from "../../shared";

import { GameLensInformationPanel } from "./GameLensInformationPanel";
import { GameLensTogglePanel } from "./GameLensTogglePanel";
import { LensDetailsRow } from "./LensDetailsRow";
import { LensDetailsName, lensPreviewStyle, splitPanelLayoutDetailsStyle } from "./LensEditDetails.styles";
import { LensEditDetailsBase } from "./LensEditDetailsBase";
import { UpdateMessage } from "./UpdateMessage";
import { UpdatingErrorMessage } from "./UpdatingErrorMessage";
import { GameLensInformationTitle, InvalidPreviewBadge, messages } from "./messages.generated";
import { type EditDetailsResult } from "./useEditDetails";
import { useGameLensToggle } from "./useGameLensToggle";

import { type GetPreSubmissionInfoQuery } from "src/app/submit/api/queries/getPreSubmissionInfo.gql-generated";

interface LensEditDetails {
    lensOrSubmissionId: string;
    data: GetAllPreSubmissionInfoWithOrgResourcesQuery | GetPreSubmissionInfoQuery | undefined;
    lensForUpdate: { name: string; description: string } | undefined;
    createChangeDetailsEvent: (source: MylensesSubmissionDetailsSource) => () => void;
    errorMessage: string | null;
    lensEditDetails: EditDetailsResult;
    hasPreviewValidationError?: boolean;
    detailsValidationError?: string;
}

export const LensEditDetails: FC<LensEditDetails> = ({
    lensOrSubmissionId,
    data,
    lensForUpdate,
    createChangeDetailsEvent,
    errorMessage,
    lensEditDetails,
    hasPreviewValidationError,
    detailsValidationError,
}) => {
    const intl = useIntl();
    const { configs } = useFeatureGates();
    const isSpectaclesSubmissionEnabled = configs[CofConfigId.MyLensesSpectaclesSubmission].enabled;
    const isMyLensesDiscoverabilityTagsV1Enabled = configs[CofConfigId.MyLensesDiscoverabilityTagsV1].enabled;
    const isSpectaclesNewFields = configs[CofConfigId.MyLensesSpectaclesRnTagsMultiPreviews].enabled;

    const {
        iconUrl,
        name,
        previewUrl: previewFromPresubmission,
        surfaceType = [SurfaceType.SurfaceTypeSnapchat],
    } = data?.getPreSubmissionInfo ?? {
        iconUrl: "",
        name: "",
        previewUrl: "",
    };

    const hasSpectacles = isSpectaclesSubmissionEnabled && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
    const hasSnapchat = surfaceType.includes(SurfaceType.SurfaceTypeSnapchat);

    const {
        lensPreviewProps,
        tagsList,
        addTagItem,
        removeTag,
        spectaclesTagsList,
        addSpectaclesTag,
        removeSpectaclesTag,
        discoverability,
        setDiscoverability,
        isGameUserProvided,
        setIsGameUserProvided,
        releaseNote,
        setReleaseNote,
    } = lensEditDetails;
    const gameLensValidationMessage = lensPreviewProps.validationMessage ?? detailsValidationError ?? null;
    const { handleGameLensToggle } = useGameLensToggle({ setIsGameUserProvided });

    const shouldShowGameLensTogglePanel = hasSnapchat;
    const previewBadgeText = hasPreviewValidationError ? (
        <InvalidPreviewBadge />
    ) : previewFromPresubmission ? (
        <LensPreviewToBeReviewed />
    ) : undefined;

    return (
        <Flexbox gap={`${Spacing.LG}px`} flexDirection="column" data-testid="lens-details-container">
            <LensDetailsRow dataTestId="lens-information-row" title={intl.formatMessage(messages.lensInformationTitle)}>
                <Flexbox gap={`${Spacing.LG}px`}>
                    <Flexbox flexDirection="column" className={lensPreviewStyle} data-testid="lens-preview-container">
                        <LensPreview
                            {...lensPreviewProps}
                            badgeText={previewBadgeText}
                            badgeStatus={hasPreviewValidationError ? Status.ERROR : undefined}
                            shouldShowUpdateMessage={!!previewFromPresubmission}
                            surfaceType={surfaceType}
                            showSpectaclesPreview
                        />
                    </Flexbox>
                    <Flexbox gap={`${Spacing.MD}px`} flexDirection="column" className={splitPanelLayoutDetailsStyle}>
                        <Control label="Lens Icon" bottomSpacing={true} data-testid="lens-icon">
                            <LensDetailsIcon iconUrl={iconUrl} />
                        </Control>
                        <Control label="Lens Name" data-testid="lens-name">
                            <LensDetailsName>{name}</LensDetailsName>
                            {name !== lensForUpdate?.name && <UpdateMessage>{lensForUpdate?.name}</UpdateMessage>}
                        </Control>
                        {shouldShowGameLensTogglePanel && (
                            <LensDetailsRow dataTestId="game-lens-row">
                                <GameLensTogglePanel
                                    isGameUserProvided={isGameUserProvided}
                                    onGameLensToggle={handleGameLensToggle}
                                />
                            </LensDetailsRow>
                        )}
                        {hasSpectacles && !hasSnapchat && (
                            <LensEditDetailsBase
                                {...lensEditDetails}
                                iconUrl={iconUrl}
                                createChangeDetailsEvent={createChangeDetailsEvent}
                                lensOrSubmissionId={lensOrSubmissionId}
                                lensForUpdate={lensForUpdate}
                                surfaceType={SurfaceType.SurfaceTypeSpectacles}
                            />
                        )}
                        {isSpectaclesNewFields && hasSpectacles && (
                            <Control label="Release Notes" data-testid="release-notes">
                                <TextArea
                                    value={releaseNote ?? undefined}
                                    onChange={setReleaseNote}
                                    autoSize={{ minRows: 2 }}
                                    maxLength={LENS_RELEASE_NOTE_MAX_CHARS}
                                />
                            </Control>
                        )}
                    </Flexbox>
                </Flexbox>
            </LensDetailsRow>

            {shouldShowGameLensTogglePanel && isGameUserProvided && (
                <LensDetailsRow dataTestId="game-lens-information-row" title={<GameLensInformationTitle />}>
                    <GameLensInformationPanel
                        lensPreviewProps={{
                            ...lensPreviewProps,
                            validationMessage: gameLensValidationMessage,
                        }}
                    />
                </LensDetailsRow>
            )}

            {hasSnapchat && (
                <LensDetailsRow
                    dataTestId="snapchat-tags-row"
                    title={intl.formatMessage(messages.snapchatTagsSectionTitle)}
                >
                    {isMyLensesDiscoverabilityTagsV1Enabled ? (
                        <LensDiscoverabilityTagsRow
                            lensOrSubmissionId={lensOrSubmissionId}
                            discoverability={discoverability}
                            setDiscoverability={setDiscoverability}
                        />
                    ) : (
                        <Tags
                            tags={tagsList}
                            addTag={addTagItem}
                            removeTag={removeTag}
                            label={intl.formatMessage(messages.lensTagsLabel, {
                                curr: tagsList.length,
                                limit: MAX_SNAPCHAT_TAGS,
                            })}
                            placeholder={intl.formatMessage(messages.lensTagsPlaceholder, {
                                limit: MAX_SNAPCHAT_TAGS,
                            })}
                            tooltipContent={intl.formatMessage(messages.lensTagsTooltip)}
                            lensSurfaceType={SurfaceType.SurfaceTypeSnapchat}
                        />
                    )}
                </LensDetailsRow>
            )}

            {isSpectaclesNewFields && hasSpectacles && (
                <LensDetailsRow
                    dataTestId="spectacles-tags-row"
                    title={intl.formatMessage(messages.spectaclesTagsSectionTitle)}
                >
                    <Tags
                        tags={spectaclesTagsList}
                        addTag={addSpectaclesTag}
                        removeTag={removeSpectaclesTag}
                        label={intl.formatMessage(messages.spectaclesTagsLabel, {
                            curr: spectaclesTagsList.length,
                            limit: MAX_SPECTACLES_TAGS,
                        })}
                        placeholder={intl.formatMessage(messages.lensTagsPlaceholder, {
                            limit: MAX_SPECTACLES_TAGS,
                        })}
                        tooltipContent={<SpectaclesTagsDescription limit={MAX_SPECTACLES_TAGS} />}
                        lensSurfaceType={SurfaceType.SurfaceTypeSpectacles}
                    />
                </LensDetailsRow>
            )}

            {hasSnapchat && (
                <LensEditDetailsBase
                    {...lensEditDetails}
                    iconUrl={iconUrl}
                    createChangeDetailsEvent={createChangeDetailsEvent}
                    lensOrSubmissionId={lensOrSubmissionId}
                    lensForUpdate={lensForUpdate}
                    surfaceType={SurfaceType.SurfaceTypeSnapchat}
                />
            )}
            {/* TODO: uncomment when we add descriptions */}
            {/* Show Tabbed when support is both Spectacles + Snapchat */}
            {/* {hasSpectacles && hasSnapchat && (
                    <>
                        <Tabs size="large" tabsClassName={lensTabSpacing}>
                            <Tab title="Snapchat">
                                <LensEditDetailsBase
                                    {...lensEditDetails}
                                    iconUrl={iconUrl}
                                    createChangeDetailsEvent={createChangeDetailsEvent}
                                    lensOrSubmissionId={lensOrSubmissionId}
                                    lensForUpdate={lensForUpdate}
                                    surfaceType={SurfaceType.SurfaceTypeSnapchat}
                                />
                            </Tab>
                            <Tab title="Spectacles">
                                <LensEditDetailsBase
                                    {...lensEditDetails}
                                    iconUrl={iconUrl}
                                    createChangeDetailsEvent={createChangeDetailsEvent}
                                    lensOrSubmissionId={lensOrSubmissionId}
                                    lensForUpdate={lensForUpdate}
                                    surfaceType={SurfaceType.SurfaceTypeSpectacles}
                                    lensDescription={lensDescription}
                                    setLensDescription={setLensDescription}
                                />
                            </Tab>
                        </Tabs>
                    </>
                )} */}

            <UpdatingErrorMessage message={errorMessage} />
        </Flexbox>
    );
};
